import { useGameStore } from '@/store/gameStore'
import {
  FACILITY_REGISTRY, getFacilityLevel, getMaxAllowedLevel, meetsRequirements, getFacilitySpec,
  type FacilityDefinition,
} from '@/config/facilityRegistry'
import { FACILITY_VISUALS, type FacilityVisual } from '@/config/campusConfig'
import { getDivisionOrNone, divisionGateLabel, divisionGateMessage } from '@/config/federationConfig'
import { CLUB_LEVELS } from '@/config/clubLevelConfig'
import { daysRemaining } from '@/config/constructionConfig'
import { EventBus, GameEvents } from '@/game/utils/EventBus'
import { EmptyStateRich } from '../components/CourtVision'
import { AngledButton } from '../components/broadcast'
import { FacilityTierImage } from '../components/campus/FacilityTierImage'
import { ModifierPills } from '../components/campus/ModifierPills'
import { BUILDING_ICONS } from '../icons/iconMap'
import {
  Warehouse, Lock, ChevronsUp, Sparkles, HardHat, CircleDot, GitBranch,
} from 'lucide-react'

const visualFor = (id: string): FacilityVisual | undefined =>
  FACILITY_VISUALS.find(v => v.id === id)

/** Spanish lock label for an unmet requirement set */
function lockLabel(def: FacilityDefinition): string {
  const req = def.requirements
  const parts: string[] = []
  if (req.clubLevel !== undefined) {
    const levelDef = CLUB_LEVELS.find(l => l.level === req.clubLevel)
    parts.push(levelDef ? `Nv.${levelDef.level}: ${levelDef.name}` : `Nivel de club ${req.clubLevel}`)
  }
  if (req.reputation !== undefined) parts.push(`Reputación ${req.reputation}+`)
  return parts.join(' · ')
}

export function BuildingsTabContent() {
  const {
    money, buildings, courts, queueConstruction, constructionQueue, clubLevel, reputation, day, league,
    buildingSpecs, chooseSpecialization,
  } = useGameStore()
  const currentDivision = getDivisionOrNone(league)

  const handleSpecialize = (def: FacilityDefinition, buildingId: string, specId: string) => {
    const spec = getFacilitySpec(def.id, specId)
    const ok = chooseSpecialization(buildingId, specId)
    EventBus.emit(GameEvents.NOTIFY, ok
      ? { message: `${spec?.emoji ?? ''} ${def.name} se especializa en ${spec?.name} (permanente)`, type: 'success' }
      : { message: 'No se pudo especializar la instalación', type: 'error' })
  }

  const notify = (ok: boolean, label: string, days: number) => {
    EventBus.emit(GameEvents.NOTIFY, ok
      ? { message: `Obra iniciada: ${label} · ${days} días`, type: 'success' }
      : { message: 'No se pudo iniciar la obra', type: 'error' })
  }

  const handleBuild = (def: FacilityDefinition) => {
    const first = def.levels[0]
    const ok = queueConstruction({
      kind: 'building',
      label: def.name,
      totalDays: first.constructionDays,
      visualId: def.id,
      tier: 1,
      buildingType: def.id,
      cost: first.cost,
    })
    notify(ok, def.name, first.constructionDays)
  }

  const handleUpgrade = (def: FacilityDefinition, buildingId: string, currentLevel: number) => {
    const next = getFacilityLevel(def.id, currentLevel + 1)
    if (!next) return
    const ok = queueConstruction({
      kind: 'building_upgrade',
      label: `${def.name} Nv.${next.level}`,
      totalDays: next.constructionDays,
      visualId: def.id,
      tier: next.assetTier,
      buildingId,
      targetLevel: next.level,
      cost: next.cost,
    })
    notify(ok, `${def.name} Nv.${next.level}`, next.constructionDays)
  }

  if (courts.length === 0 && buildings.length === 0) {
    return (
      <EmptyStateRich
        icon={Warehouse}
        title="Primero las pistas, luego el club"
        sub="Construye tu primera pista y podrás levantar recepción, tienda y mucho más"
      />
    )
  }

  return (
    <div className="facility-grid">
      {Object.values(FACILITY_REGISTRY).map(def => {
        const visual = visualFor(def.id)
        const icon = BUILDING_ICONS[def.id] ?? CircleDot
        const building = buildings.find(b => b.type === def.id)
        const unlocked = meetsRequirements(def.requirements, clubLevel, reputation)
        const trackMax = def.levels.length

        // Pending works for this facility (a fresh build, or an upgrade in progress)
        const buildJob = constructionQueue.find(j => j.kind === 'building' && j.buildingType === def.id)
        const upgradeJob = building
          ? constructionQueue.find(j => j.kind === 'building_upgrade' && j.buildingId === building.id)
          : undefined
        const pendingJob = buildJob ?? upgradeJob

        const currentLevel = building ? getFacilityLevel(def.id, building.level) : undefined
        const nextLevel = building ? getFacilityLevel(def.id, building.level + 1) : undefined
        const maxAllowed = getMaxAllowedLevel(def.id, clubLevel, currentDivision)
        const atTrackMax = building ? building.level >= trackMax : false
        // Soft cap: more levels exist on the track, but the club must grow
        // (club level) or earn its Licencia Federativa (league division) first
        const softCapped = building ? !atTrackMax && building.level >= maxAllowed : false
        const nextReq = nextLevel?.requirements
        const gatedByDivision = softCapped && nextReq?.leagueDivision !== undefined
          && currentDivision > nextReq.leagueDivision
          && clubLevel >= (nextReq.clubLevel ?? 0)

        // Tier shown in the illustration: current level if built, tier-1
        // preview if buildable, placeholder if locked.
        const shownTier = building ? (currentLevel?.assetTier ?? building.level) : unlocked ? 1 : -1
        const isPreview = !building && unlocked && !buildJob
        const descriptor = building ? currentLevel?.caption : def.description

        return (
          <div
            key={def.id}
            className="facility-card"
            style={!building && !unlocked ? { opacity: 0.6 } : undefined}
          >
            {visual && (
              <div style={{ position: 'relative' }}>
                <FacilityTierImage
                  visual={visual}
                  tier={shownTier}
                  icon={icon}
                  style={(isPreview || pendingJob) ? { opacity: 0.5, filter: 'grayscale(0.4)' } : undefined}
                />
                {pendingJob && (
                  <div className="facility-building-pending">
                    <HardHat size={20} />
                    <span className="fbp-eta">
                      {daysRemaining(pendingJob, day) === 0 ? 'Listo hoy' : `${daysRemaining(pendingJob, day)} días`}
                    </span>
                    <span className="fbp-label">{buildJob ? 'En construcción' : 'Mejorando'}</span>
                  </div>
                )}
              </div>
            )}

            <div className="facility-body">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.4rem' }}>
                <span className="card-title" style={{ fontSize: '0.85rem' }}>{def.name}</span>
                {building
                  ? <span className="chip-angled tone-gold">Nv. {building.level}/{trackMax}</span>
                  : isPreview && <span className="chip-angled">Vista previa</span>}
              </div>

              <div className="meta-text" style={{ marginTop: '0.15rem' }}>{descriptor}</div>

              {/* Active effects of the current level */}
              {currentLevel && !pendingJob && (
                <ModifierPills mods={currentLevel.modifiers} style={{ marginTop: '0.4rem' }} />
              )}

              <div className="facility-status" style={{ marginTop: '0.5rem' }}>
                {pendingJob ? (
                  <span className="pill pill-warning">
                    <HardHat size={10} /> En obras · {daysRemaining(pendingJob, day)}d
                  </span>
                ) : (
                  <>
                    {isPreview && (
                      <AngledButton tone="gold" onClick={() => handleBuild(def)} disabled={money < def.levels[0].cost}>
                        Construir · {def.levels[0].cost.toLocaleString('es-ES')}€
                      </AngledButton>
                    )}

                    {building && nextLevel && !softCapped && (
                      <AngledButton
                        tone="gold"
                        onClick={() => handleUpgrade(def, building.id, building.level)}
                        disabled={money < nextLevel.cost}
                      >
                        <ChevronsUp size={13} /> Mejorar · {nextLevel.cost.toLocaleString('es-ES')}€
                      </AngledButton>
                    )}

                    {building && softCapped && (
                      gatedByDivision ? (
                        <span
                          className="pill pill-warning"
                          title={divisionGateMessage(nextReq!.leagueDivision!)}
                        >
                          <Lock size={10} /> {divisionGateLabel(nextReq!.leagueDivision!)}
                        </span>
                      ) : (
                        <span className="pill pill-warning">
                          <Lock size={10} /> Requiere nivel de club {nextReq?.clubLevel}
                        </span>
                      )
                    )}

                    {building && atTrackMax && (
                      <span className="pill pill-success">
                        <Sparkles size={10} />
                        {buildingSpecs[building.id]
                          ? `${getFacilitySpec(def.id, buildingSpecs[building.id])?.emoji ?? ''} ${getFacilitySpec(def.id, buildingSpecs[building.id])?.name ?? 'Nivel máximo'}`
                          : 'Nivel máximo'}
                      </span>
                    )}

                    {!building && !unlocked && (
                      <span className="pill pill-warning">
                        <Lock size={10} /> {lockLabel(def)}
                      </span>
                    )}
                  </>
                )}

                {building && !pendingJob && currentLevel && (
                  <span className="meta-text" style={{ marginLeft: 'auto' }}>
                    mant. {currentLevel.maintenancePerWeek}€/sem
                  </span>
                )}
              </div>

              {/* What the next upgrade brings */}
              {building && nextLevel && !softCapped && !pendingJob && (
                <div style={{
                  marginTop: '0.4rem', display: 'flex', alignItems: 'center', gap: '0.3rem',
                  fontSize: '0.68rem', color: 'var(--gold)', flexWrap: 'wrap',
                }}>
                  <ChevronsUp size={11} />
                  <span style={{ color: 'var(--text-secondary)' }}>Próximo:</span> {nextLevel.caption}
                </div>
              )}

              {/* Terminal specialization: pick ONE branch at max level */}
              {building && atTrackMax && def.specs && !buildingSpecs[building.id] && !pendingJob && (
                <div style={{ marginTop: '0.5rem' }}>
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: '0.3rem',
                    fontSize: '0.68rem', color: 'var(--gold)', marginBottom: '0.3rem',
                  }}>
                    <GitBranch size={11} />
                    <span style={{ color: 'var(--text-secondary)' }}>Elige una especialización (permanente):</span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                    {def.specs.map(spec => (
                      <button
                        key={spec.id}
                        onClick={() => handleSpecialize(def, building.id, spec.id)}
                        className="row-tile"
                        style={{ padding: '0.4rem 0.5rem' }}
                        title={spec.description}
                      >
                        <div className="row-tile-main">
                          <div className="row-tile-title" style={{ fontSize: '0.75rem' }}>
                            {spec.emoji} {spec.name}
                          </div>
                          <div className="row-tile-sub" style={{ fontSize: '0.64rem' }}>{spec.description}</div>
                          <ModifierPills mods={spec.modifiers} style={{ marginTop: '0.2rem' }} />
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Chosen spec: show its permanent effects */}
              {building && buildingSpecs[building.id] && !pendingJob && (
                <ModifierPills
                  mods={getFacilitySpec(def.id, buildingSpecs[building.id])?.modifiers ?? []}
                  style={{ marginTop: '0.35rem' }}
                />
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}
